import { useState } from 'react';
import { ListPage } from '../../components/ListPage';
import type { Column } from '../../components/ListPage';
import { KeyValue, Pill, Spinner, FormGrid, Field, TextInput, NumberInput, LineTable } from '../../components/ui';
import { SearchSelect } from '../../components/SearchSelect';
import { useDetail } from '../../lib/useDetail';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { can } from '../../lib/permissions';
import { num, planStatus } from '../../lib/format';
import type { MaterialPlan, MaterialPlanLine, Item } from '../../lib/types';

// 核心服务写接口返回 { data: payload } 信封；防御性解包。
function unwrap<T>(body: unknown): T {
  if (body && typeof body === 'object' && 'data' in (body as Record<string, unknown>)) {
    return (body as { data: T }).data;
  }
  return body as T;
}

interface LineRow {
  itemId: string; label: string; sort: string; quantity: string; uom: string;
  loss: string; batch: boolean; notes: string;
}

const emptyLine = (): LineRow => ({ itemId: '', label: '', sort: '', quantity: '1', uom: 'Kg', loss: '0', batch: false, notes: '' });

function lineRowsFromPlan(p: MaterialPlan): LineRow[] {
  return (p.lines ?? []).map((l) => ({
    itemId: l.item_id,
    label: `${l.item_code ?? ''} ${l.item_name_zh ?? ''}`.trim(),
    sort: String(l.sort_order ?? ''),
    quantity: String(l.quantity ?? ''),
    uom: l.uom ?? '',
    loss: l.loss_rate == null ? '0' : String(l.loss_rate),
    batch: !!l.batch_required,
    notes: l.notes ?? '',
  }));
}

function linePayload(rows: LineRow[]) {
  return rows.filter((l) => l.itemId && Number(l.quantity) > 0).map((l, index) => {
    const body: Record<string, unknown> = {
      item_id: l.itemId,
      quantity: Number(l.quantity),
      uom: l.uom || 'Unidad',
      loss_rate: l.loss === '' ? 0 : Number(l.loss),
      batch_required: l.batch,
      sort_order: l.sort === '' ? index : Number(l.sort),
    };
    if (l.notes) body.notes = l.notes;
    return body;
  });
}

// 新建 / 编辑草稿（仅草稿且未被引用可编辑）。
function PlanForm({ initial, onDone, onCancel }: { initial: MaterialPlan | null; onDone: () => void; onCancel: () => void }) {
  const [code, setCode] = useState(initial?.code ?? '');
  const [nameZh, setNameZh] = useState(initial?.name_zh ?? '');
  const [nameEs, setNameEs] = useState(initial?.name_es ?? '');
  const [itemId, setItemId] = useState<string | null>(initial?.item_id ?? null);
  const [itemLabel, setItemLabel] = useState(initial?.item_id ? `${initial.item_code ?? ''} ${initial.item_name_zh ?? ''}`.trim() : '');
  const [notes, setNotes] = useState(initial?.notes ?? '');
  const [rows, setRows] = useState<LineRow[]>(initial ? lineRowsFromPlan(initial) : [emptyLine()]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const setRow = (i: number, patch: Partial<LineRow>) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));

  const submit = async () => {
    setErr(null);
    if (!code.trim()) { setErr('请填写方案编码'); return; }
    if (!nameZh.trim()) { setErr('请填写方案名称'); return; }
    const lines = linePayload(rows);
    if (!lines.length) { setErr('至少需要一项有效用料（选择物料且数量>0）'); return; }
    setBusy(true);
    try {
      const payload = { code: code.trim(), name_zh: nameZh.trim(), name_es: nameEs || null, item_id: itemId || null, notes: notes || null, lines };
      if (initial) unwrap<MaterialPlan>(await api.put(`/api/material-plans/${initial.id}`, payload));
      else unwrap<MaterialPlan>(await api.post('/api/material-plans', payload));
      onDone();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '保存失败');
    } finally { setBusy(false); }
  };

  return (
    <section className="panel" style={{ marginBottom: 'var(--gap)' }}>
      <div className="panel-head"><div><h2>{initial ? `编辑材料方案草稿 · ${initial.code}` : '新建材料方案'}</h2>
        <div className="desc">写入 /api/material-plans · 定义真正计入的物料消耗（粉末/焊丝等）+ 单方用量 + 损耗率。计划耗用 = 单方用量 ×(1+损耗率)× 生产数量。新建保存为草稿，批准后方可用于新工单（建单时冻结为工单材料快照）。</div></div></div>
      <div className="panel-body">
        {err ? <div className="notice bad" style={{ marginBottom: 12 }}>{err}</div> : null}
        <FormGrid>
          <Field label="方案编码" required><TextInput value={code} onChange={(e) => setCode(e.target.value)} placeholder="如 MP-CLAD-POWDER" /></Field>
          <Field label="名称（中）" required><TextInput value={nameZh} onChange={(e) => setNameZh(e.target.value)} /></Field>
          <Field label="名称（西语）"><TextInput value={nameEs} onChange={(e) => setNameEs(e.target.value)} /></Field>
          <Field label="适用产出物料（选填，留空为通用方案）">
            <SearchSelect<Item> endpoint="/api/items" value={itemId} label={itemLabel}
              onChange={(row) => { setItemId(row?.id ?? null); setItemLabel(row ? `${row.code} ${row.name_zh}` : ''); }}
              optionTitle={(it) => `${it.code} · ${it.name_zh}`} placeholder="检索适用物料…" />
          </Field>
          <Field label="备注" span><TextInput value={notes} onChange={(e) => setNotes(e.target.value)} /></Field>
        </FormGrid>

        <div className="detail-head" style={{ margin: '14px 0 6px' }}><h3 style={{ fontSize: 14 }}>用料明细</h3><div className="spacer" />
          <button className="btn link" onClick={() => setRows((rs) => [...rs, emptyLine()])}>+ 添加用料</button>
        </div>
        <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>损耗率填小数（如 0.05 = 5%）。批次默认“可空”，勾选后强制相关领料填写批次（激光熔覆粉末追溯）。</div>
        {rows.map((l, i) => (
          <div key={i} className="form-row" style={{ display: 'grid', gridTemplateColumns: '2.4fr 0.7fr 1fr 0.9fr 1fr 0.9fr auto auto', gap: 8, marginBottom: 6, alignItems: 'end' }}>
            <Field label={`用料 ${i + 1}`}>
              <SearchSelect<Item> endpoint="/api/items" value={l.itemId || null} label={l.label}
                onChange={(row) => setRow(i, { itemId: row?.id ?? '', label: row ? `${row.code} ${row.name_zh}` : '', uom: row?.stock_uom ?? l.uom })}
                optionTitle={(it) => `${it.code} · ${it.name_zh}`} placeholder="检索物料…" />
            </Field>
            <Field label="排序"><NumberInput value={l.sort} onChange={(e) => setRow(i, { sort: e.target.value })} /></Field>
            <Field label="单方用量" required><NumberInput value={l.quantity} onChange={(e) => setRow(i, { quantity: e.target.value })} /></Field>
            <Field label="单位" required><TextInput value={l.uom} onChange={(e) => setRow(i, { uom: e.target.value })} /></Field>
            <Field label="损耗率"><NumberInput value={l.loss} onChange={(e) => setRow(i, { loss: e.target.value })} /></Field>
            <label style={{ display: 'flex', gap: 4, alignItems: 'center', fontSize: 12, paddingBottom: 8 }}>
              <input type="checkbox" checked={l.batch} onChange={(e) => setRow(i, { batch: e.target.checked })} />强制批次
            </label>
            <Field label="备注"><TextInput value={l.notes} onChange={(e) => setRow(i, { notes: e.target.value })} /></Field>
            <button className="btn" onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))} title="删除该用料">×</button>
          </div>
        ))}

        <div className="detail-actions">
          <button className="btn primary" onClick={submit} disabled={busy}>{busy ? '提交中…' : (initial ? '保存草稿' : '创建草稿')}</button>
          <button className="btn" onClick={onCancel} disabled={busy}>取消</button>
        </div>
      </div>
    </section>
  );
}

function PlanDetail({ row, onChanged, onEdit }: { row: MaterialPlan; onChanged: () => void; onEdit: (p: MaterialPlan) => void }) {
  const { user } = useAuth();
  // [PATCH-6] 详情按钮同样需模块可见（与后端 view 白名单一致）
  const canCreate = can(user, 'material-plans', 'view') && can(user, 'material-plans', 'create');
  const canTransition = can(user, 'material-plans', 'view') && can(user, 'material-plans', 'transition');
  const { data, loading, error, reload } = useDetail<MaterialPlan>(`/api/material-plans/${row.id}`);
  const [busy, setBusy] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const p = data ?? row;
  const lines = p.lines ?? [];
  const used = !!p.used;
  const editable = canCreate && p.status === 'draft' && !used;

  const act = async (kind: 'approve' | 'deactivate' | 'copy') => {
    setErr(null); setBusy(kind);
    try {
      if (kind === 'copy') unwrap<MaterialPlan>(await api.post(`/api/material-plans/${p.id}/copy`, {}));
      else unwrap<MaterialPlan>(await api.post(`/api/material-plans/${p.id}/status`, { status: kind === 'approve' ? 'approved' : 'deactivated' }));
      reload(); onChanged();
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '操作失败');
    } finally { setBusy(null); }
  };

  return (
    <div className="detail" onClick={(e) => e.stopPropagation()}>
      <div className="detail-head">
        <span className="id">{p.code}</span>
        <h3>{p.name_zh}</h3>
        <Pill tone={planStatus(p.status).tone} label={planStatus(p.status).label} />
        <Pill tone={used ? 'info' : 'neutral'} label={used ? '已被引用' : '未引用'} />
        <div className="spacer" />
        {loading ? <Spinner label="刷新…" /> : null}
      </div>
      {error ? <div className="notice bad">{error}</div> : null}
      {err ? <div className="notice bad">{err}</div> : null}
      <div className="kv-grid">
        <KeyValue k="版本">v{p.version}{p.parent_id ? '（派生）' : ''}</KeyValue>
        <KeyValue k="用料项数">{lines.length}</KeyValue>
        <KeyValue k="适用产出物料">{`${p.item_code ?? ''} ${p.item_name_zh ?? ''}`.trim() || '通用方案'}</KeyValue>
        <KeyValue k="西语名称">{p.name_es || '—'}</KeyValue>
        <KeyValue k="备注">{p.notes || '—'}</KeyValue>
        <KeyValue k="批准时间">{p.approved_at ? p.approved_at.slice(0, 10) : '—'}</KeyValue>
      </div>
      <div className="k" style={{ fontSize: 11, color: 'var(--ink-3)', margin: '10px 0 4px' }}>用料明细（计划耗用 = 单方用量 ×(1+损耗率)× 生产数量，工单建单时冻结）</div>
      <LineTable<MaterialPlanLine>
        rows={lines}
        emptyText="暂无用料"
        columns={[
          { header: '序', align: 'right', render: (l) => <span className="num">{l.sort_order}</span> },
          { header: '物料', render: (l) => `${l.item_code ?? ''} ${l.item_name_zh ?? ''}`.trim() || '—' },
          { header: '单方用量', align: 'right', render: (l) => <span className="num">{num(l.quantity, 3)}</span> },
          { header: '单位', render: (l) => <span className="muted">{l.uom}</span> },
          { header: '损耗率', align: 'right', render: (l) => <span className="num">{num(l.loss_rate * 100, 1)}%</span> },
          { header: '批次', render: (l) => (l.batch_required ? <Pill tone="warn" label="强制批次" /> : <span className="muted">可空</span>) },
        ]}
      />
      {canCreate || canTransition ? (
        <div className="detail-actions" style={{ marginTop: 10 }}>
          {editable ? <button className="btn" onClick={() => onEdit(p)}>编辑草稿</button> : null}
          {canTransition && p.status === 'draft' ? <button className="btn primary" onClick={() => act('approve')} disabled={!!busy}>{busy === 'approve' ? '提交中…' : '批准'}</button> : null}
          {canTransition && p.status === 'approved' ? <button className="btn" onClick={() => act('deactivate')} disabled={!!busy}>{busy === 'deactivate' ? '提交中…' : '停用'}</button> : null}
          {canCreate ? <button className="btn" onClick={() => act('copy')} disabled={!!busy}>{busy === 'copy' ? '提交中…' : (used ? '复制为新版本' : '复制版本')}</button> : null}
        </div>
      ) : null}
      {editable ? <div className="muted" style={{ fontSize: 11.5, marginTop: 6 }}>已批准版本或被工单/快照引用的方案不可直接覆盖，请用“复制为新版本”后修改。</div> : null}
    </div>
  );
}

export function MaterialPlansPage() {
  const { user } = useAuth();
  const [reloadSignal, setReloadSignal] = useState(0);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<MaterialPlan | null>(null);
  // [PATCH-6] 创建按钮需同时具备：模块可见（与后端 view 白名单一致）+ create 权限。
  //   仅判 create 会让「读被拒但按钮可见」的账号（如 finance）拿到 403。
  const canView = can(user, 'material-plans', 'view');
  const canCreate = canView && can(user, 'material-plans', 'create');
  // [PATCH-5] 列表筛选：适用物料 itemId / 启用状态 active（与后端参数同名）
  const [filterItemId, setFilterItemId] = useState('');
  const [filterActive, setFilterActive] = useState('');
  const [filterItemIdLabel, setFilterItemIdLabel] = useState('');

  const columns: Column<MaterialPlan>[] = [
    { header: '编码', render: (p) => <span className="num strong">{p.code}</span> },
    // 「适用产出物料」列已移除（2026-10-29 现场要求：没什么必要）。
    // 该列内容（item_code + item_name_zh）与「编码」「名称」高度重复 ——
    // 编码里已含产出物料号（MB-BOM-101001002001-003），名称末尾也是同一物料名，
    // 三列并排等于把同一信息读三遍，还把右侧「状态/引用」挤出可视区。
    // 适用物料仍在详情页与新建/编辑表单里完整可见。
    { header: '名称', render: (p) => p.name_zh },
    { header: '版本', render: (p) => <span className="muted">v{p.version}</span> },
    { header: '用料项数', align: 'right', render: (p) => <span className="num">{p.line_count ?? 0}</span> },
    { header: '状态', render: (p) => <Pill tone={planStatus(p.status).tone} label={planStatus(p.status).label} /> },
    { header: '引用', render: (p) => (p.used ? <Pill tone="info" label="已引用" /> : <span className="muted">—</span>) },
  ];

  const closeAndReload = () => { setCreating(false); setEditing(null); setReloadSignal((n) => n + 1); };

  return (
    <>
      {creating ? <PlanForm initial={null} onDone={closeAndReload} onCancel={() => setCreating(false)} /> : null}
      {editing ? <PlanForm initial={editing} onDone={closeAndReload} onCancel={() => setEditing(null)} /> : null}
      <ListPage<MaterialPlan>
        title="材料方案"
        desc="真正计入的物料消耗 · /api/material-plans。生命周期 草稿→批准→停用；仅已批准可用于新工单，被引用方案只能复制为新版本再改。展开查看/维护用料、单方用量、损耗率与批次要求"
        endpoint="/api/material-plans"
        columns={columns}
        rowId={(p) => p.id}
        showYear={false}
        searchPlaceholder="搜索方案编码 / 名称…"
        reloadSignal={reloadSignal}
        // [PATCH-5] 适用物料 / 启用状态筛选（参数名与后端一致）
        filters={(r) => (
          <>
            <div className="field" style={{ minWidth: 220 }}>
              <label>适用产出物料</label>
              <SearchSelect<Item>
                endpoint="/api/items"
                value={filterItemId}
                label={filterItemIdLabel}
                onChange={(row) => {
                  const id = row?.id ?? null;
                  setFilterItemId(id ?? '');
                  r.setItemId(id ?? '');
                  setFilterItemIdLabel(row ? `${row.code ?? ''} ${row.name_zh ?? ''}`.trim() : '');
                }}
                placeholder="全部物料"
                optionTitle={(it) => `${it.code ?? ''} ${it.name_zh ?? ''}`.trim() || it.id}
              />
            </div>
            <div className="field">
              <label>启用状态</label>
              <select className="input" value={filterActive}
                onChange={(e) => { setFilterActive(e.target.value); r.setActive(e.target.value); }}>
                <option value="">全部</option>
                <option value="1">启用</option>
                <option value="0">已停用</option>
              </select>
            </div>
          </>
        )}
        actions={canCreate ? (
          <button className="btn primary" onClick={() => { setEditing(null); setCreating((v) => !v); }}>{creating ? '收起新建' : '新建方案'}</button>
        ) : undefined}
        renderDetail={(row) => <PlanDetail row={row} onChanged={() => setReloadSignal((n) => n + 1)} onEdit={(p) => { setCreating(false); setEditing(p); }} />}
      />
    </>
  );
}
